import { useEffect, useState } from 'react'
import { useStages } from '@/features/admin/stages/hooks'
import { useOverrideStage } from '../hooks'

type Props = {
  contractId: number
  configurationCode: string
  currentStageCode: string
  open: boolean
  onClose: () => void
}

const MIN_REASON = 3
const MAX_REASON = 500

/**
 * Dialog "Zmiana etapu (admin)" -- pozwala wskazać etap docelowy (dowolny inny niż aktualny)
 * i podać powód (min {@value #MIN_REASON} znaków). Po sukcesie zamyka się; błąd backendu
 * zostaje w mutation.error -- panel pokazuje krótki komunikat.
 */
export function OverrideStageDialog({ contractId, configurationCode, currentStageCode, open, onClose }: Props) {
  const stagesQ = useStages(configurationCode)
  const stages = (stagesQ.data ?? []).filter(s => s.code !== currentStageCode)
  const mutation = useOverrideStage(contractId)

  const [target, setTarget] = useState<string>('')
  const [reason, setReason] = useState<string>('')

  useEffect(() => {
    if (!open) {
      setTarget('')
      setReason('')
      mutation.reset()
    }
    // Tylko `open` jest reaktywnym triggerem. `mutation` (obiekt z react-query)
    // dostaje nową tożsamość co render - trzymanie go w deps powodowało pętlę
    // (effect -> mutation.reset() -> re-render -> nowy mutation -> effect -> ...).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const isValid = target.length > 0 && reason.trim().length >= MIN_REASON
  const errorMessage = mutation.error
    ? ((mutation.error as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Błąd zmiany etapu')
    : null

  const handleSubmit = () => {
    if (!isValid) return
    mutation.mutate(
      { targetStageCode: target, reason: reason.trim() },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="override-stage-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-lg w-full max-w-md p-5 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <h2 id="override-stage-title" className="text-lg font-semibold">
          Zmiana etapu (admin)
        </h2>

        <div className="space-y-1">
          <label htmlFor="override-stage-target" className="text-sm font-medium">
            Docelowy etap
          </label>
          <select
            id="override-stage-target"
            value={target}
            onChange={e => setTarget(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value="">-- wybierz --</option>
            {stages.map(s => (
              <option key={s.code} value={s.code}>{s.labelPl}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label htmlFor="override-stage-reason" className="text-sm font-medium">
            Powód (min {MIN_REASON} znaków)
          </label>
          <textarea
            id="override-stage-reason"
            value={reason}
            maxLength={MAX_REASON}
            onChange={e => setReason(e.target.value)}
            placeholder="Dlaczego pomijamy normalną ścieżkę?"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm h-24"
          />
        </div>

        {errorMessage && (
          <div className="text-sm text-red-600">{errorMessage}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || mutation.isPending}
            className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
          >
            Zatwierdź skok
          </button>
        </div>
      </div>
    </div>
  )
}
